import React, { useState } from 'react';
import { FileText, Download, Loader2, Sparkles, Mail, CheckCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ReportGeneratorProps {
  beforeImage?: string;
  afterImage?: string;
  changeDetectionData?: any;
  aoi?: any;
  beforeDate?: string;
  afterDate?: string;
  beforeReleaseId?: string | number;
  afterReleaseId?: string | number;
  locationName?: string;
}

const ReportGenerator: React.FC<ReportGeneratorProps> = ({
  beforeImage,
  afterImage,
  changeDetectionData,
  aoi,
  beforeDate,
  afterDate,
  beforeReleaseId,
  afterReleaseId,
  locationName,
}) => {
  const [generating, setGenerating] = useState(false);
  const [reportUrl, setReportUrl] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [pdfAvailable, setPdfAvailable] = useState(false);
  const { toast } = useToast();

  const generateReport = async () => {
    if (!beforeImage || !afterImage || !changeDetectionData) {
      toast({
        title: 'Cannot generate report',
        description: 'Please complete change detection analysis first',
        variant: 'destructive',
      });
      return;
    }

    setGenerating(true);
    setReportUrl(null);
    setEmailSent(false);
    setPdfAvailable(false);

    try {
      const response = await fetch('/api/geoint/wayback/generate-report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('access_token') || localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({
          before_image: beforeImage,
          after_image: afterImage,
          change_detection: changeDetectionData,
          aoi: aoi,
          before_date: beforeDate,
          after_date: afterDate,
          before_release_id: beforeReleaseId ? String(beforeReleaseId) : undefined,
          after_release_id: afterReleaseId ? String(afterReleaseId) : undefined,
          location_description: locationName,
        }),
      });

      if (!response.ok) {
        throw new Error('Report generation failed');
      }

      const data = await response.json();
      setReportUrl(data.report_url);
      setPdfAvailable(data.pdf_available === true);
      setEmailSent(data.email_sent === true);
    } catch (error) {
      console.error('Report generation error:', error);
      toast({
        title: 'Report generation failed',
        description: 'Failed to generate report. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  const downloadPdf = () => {
    if (reportUrl) {
      // Open PDF download in new tab
      window.open(reportUrl, '_blank');
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Intelligence Report
        </h3>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Sparkles className="w-4 h-4" />
          <span>AI-Powered Analysis</span>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        Generate a professional PDF intelligence report with AI-powered analysis, satellite imagery, and SYCEK branding.
      </p>

      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm">
          <div className={`w-2 h-2 rounded-full ${beforeImage ? 'bg-green-500' : 'bg-gray-300'}`} />
          <span>Before Image: {beforeImage ? 'Ready' : 'Not available'}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <div className={`w-2 h-2 rounded-full ${afterImage ? 'bg-green-500' : 'bg-gray-300'}`} />
          <span>After Image: {afterImage ? 'Ready' : 'Not available'}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <div className={`w-2 h-2 rounded-full ${changeDetectionData ? 'bg-green-500' : 'bg-gray-300'}`} />
          <span>Change Detection: {changeDetectionData ? 'Complete' : 'Not available'}</span>
        </div>
      </div>

      <button
        onClick={generateReport}
        disabled={generating || !beforeImage || !afterImage || !changeDetectionData}
        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 disabled:bg-muted disabled:text-muted-foreground text-primary-foreground rounded-md text-sm font-medium transition-colors"
      >
        {generating ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Generating PDF Report...
          </>
        ) : (
          <>
            <FileText className="w-4 h-4" />
            Generate Intelligence Report
          </>
        )}
      </button>

      {reportUrl && (
        <div className="space-y-3">
          <button
            onClick={downloadPdf}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium transition-colors"
          >
            <Download className="w-4 h-4" />
            {pdfAvailable ? 'Download PDF Report' : 'Download Report'}
          </button>

          {emailSent && (
            <div className="flex items-center gap-2 text-sm text-green-400 bg-green-950/30 border border-green-800/40 rounded-md px-3 py-2">
              <Mail className="w-4 h-4" />
              <span>Report sent to your email</span>
              <CheckCircle className="w-3.5 h-3.5 ml-auto" />
            </div>
          )}

          {!emailSent && reportUrl && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground px-1">
              <Mail className="w-3.5 h-3.5" />
              <span>Email delivery requires SMTP configuration</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportGenerator;
